import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Box, Grid, Alert } from "@mui/material";
import {
  DataTable,
  ColumnConfig,
  RendererType,
} from "../components/DataTable";
import FilterBar, { FilterValues } from "../components/FilterBar";
import RowDetailsDialog from "../components/RowDetailsDialog";
import { useSorting } from "../hooks/useSorting";
import { useFilterParser } from "../hooks/useFilterParser";

interface ByServiceOrderProps {
  onLastUpdatedChange?: (lastUpdated: string) => void;
  filterState: any;
  onFilterChange: any;
}

export interface ByServiceOrderRef {
  refresh: () => void;
  getLastUpdated: () => string;
}

const ByServiceOrder = forwardRef<ByServiceOrderRef, ByServiceOrderProps>(
  ({ onLastUpdatedChange, filterState, onFilterChange }, ref) => {
    const [edFeed, setEdFeed] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [lastUpdated, setLastUpdated] = useState<string>("N/A");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(null);

    // Use custom hooks
    const { sortColumn, sortDirection, handleSort } = useSorting();
    const { parseFilterString, toCommaSeparated, toJsonString } =
      useFilterParser();

    // Local filter state
    const [localFilters, setLocalFilters] = useState<FilterValues>({
      acctgDt: [],
      planningArea: [],
      techLdap: [],
      quotedJobcode: [],
      declineReason: [],
      llmClassification: [],
      createSource: [],
      applianceType: [],
      orderNumber: "",
      customerRating: [],
    });

    // Sync local filter state with parent filterState on mount and when filterState changes
    useEffect(() => {
      setLocalFilters({
        acctgDt: parseFilterString(filterState.acctgDt),
        planningArea: parseFilterString(filterState.filter),
        techLdap: parseFilterString(filterState.techLdap),
        quotedJobcode: parseFilterString(filterState.quotedJobcode),
        declineReason: parseFilterString(filterState.declineReason),
        llmClassification: parseFilterString(filterState.llmChatClassification),
        createSource: parseFilterString(filterState.createSource),
        applianceType: parseFilterString(filterState.applianceType),
        orderNumber: filterState.orderNumber || "",
        customerRating: parseFilterString(filterState.customerRating),
      });
    }, [filterState, parseFilterString]);

    // Handle filter changes from FilterBar
    const handleFilterChange = (filters: FilterValues) => {
      setLocalFilters(filters);
      // Convert arrays to JSON strings to preserve values with commas
      onFilterChange(
        true,
        filterState.startDate,
        filterState.endDate,
        toJsonString(filters.planningArea),
        0,
        filterState.limit,
        toJsonString(filters.quotedJobcode),
        toJsonString(filters.techLdap),
        toJsonString(filters.acctgDt),
        toJsonString(filters.declineReason),
        toJsonString(filters.applianceType),
        toJsonString(filters.createSource),
        toJsonString(filters.llmClassification),
        filters.orderNumber,
        toJsonString(filters.customerRating)
      );
    };

    // Fetch data
    const fetchData = useCallback(
      async (isRefresh = false, signal?: AbortSignal) => {
        const {
          startDate,
          endDate,
          filter,
          quotedJobcode,
          techLdap,
          acctgDt,
          declineReason,
          applianceType,
          createSource,
          llmChatClassification,
          orderNumber,
          customerRating,
          page,
          limit,
        } = filterState;

        try {
          setError(null);
          // Never clear data to maintain table height and smooth transitions
          // Data will be replaced when new data arrives

          if (isRefresh) {
            setRefreshing(true);
          } else {
            setLoading(true);
          }

          const feedResponse = await fetch(`api/snowflake/by-service-order`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            signal,
            body: JSON.stringify({
              filter: toCommaSeparated(filter),
              quotedJobcode: toCommaSeparated(quotedJobcode),
              techLdap: toCommaSeparated(techLdap),
              acctgDt: toCommaSeparated(acctgDt),
              declineReason: toCommaSeparated(declineReason),
              applianceType: toCommaSeparated(applianceType),
              createSource: toCommaSeparated(createSource),
              llmChatClassification: toCommaSeparated(llmChatClassification),
              orderNumber: orderNumber || "",
              customerRating: toCommaSeparated(customerRating),
              startDate,
              endDate,
              page,
              limit,
              sortColumn: sortColumn,
              sortDirection: sortDirection,
            }),
          });

          const feedData = await feedResponse.json();
          if (feedData.data) {
            setEdFeed(feedData.data);
            setTotalCount(feedData.pagination.total);
          } else {
            setEdFeed(feedData || []);
            setTotalCount(feedData?.length || 0);
          }

          const currentTime = new Date().toLocaleString();
          setLastUpdated(currentTime);
          if (onLastUpdatedChange) {
            onLastUpdatedChange(currentTime);
          }
        } catch (err: any) {
          // Don't show error if request was aborted
          if (err.name !== "AbortError") {
            setError("Failed to fetch data");
            console.error("Error fetching data:", err);
            setEdFeed([]);
            setTotalCount(0);
            setLoading(false);
            setRefreshing(false);
          }
          // If aborted, keep loading state as is
        } finally {
          // Only set loading to false if not aborted
          if (!signal?.aborted) {
            setLoading(false);
            setRefreshing(false);
          }
        }
      },
      [filterState, sortColumn, sortDirection, onLastUpdatedChange, toCommaSeparated]
    );

    useEffect(() => {
      const abortController = new AbortController();
      fetchData(false, abortController.signal);

      return () => {
        abortController.abort();
      };
    }, [filterState, sortColumn, sortDirection, fetchData]);

    useImperativeHandle(
      ref,
      () => ({
        refresh: () => fetchData(true),
        getLastUpdated: () => lastUpdated,
      }),
      [fetchData, lastUpdated]
    );

    // Handle row click
    const handleRowClick = (row: any) => {
      setSelectedRow(row);
      setDialogOpen(true);
    };

    // Handle dialog close
    const handleDialogClose = () => {
      setDialogOpen(false);
    };

    // Column configurations
    const columnConfigs: ColumnConfig[] = [
      {
        name: "Decline Date (CT)",
        field: "Decline Date",
        minWidth: "120px",
        maxWidth: "120px",
        width: "120px",
        align: "left",
      },
      {
        name: "Order Number",
        minWidth: "130px",
        maxWidth: "130px",
        width: "130px",
        onClick: (value) => {
          if (value) {
            window.open(
              `https://searshc.sharepoint.com/sites/Koyfin/Shared%20Documents/index.aspx/home/order-lookup?ORDERNUM=${value}`,
              "_blank"
            );
          }
        },
        align: "right",
      },
      {
        name: "Total Quoted",
        rendererType: RendererType.CURRENCY,
        minWidth: "16ch",
        maxWidth: "16ch",
        width: "16ch",
        align: "right",
      },
      {
        name: "Labor Quoted",
        rendererType: RendererType.CURRENCY,
        minWidth: "75px",
        maxWidth: "75px",
        width: "75px",
        align: "right",
      },
      {
        name: "Parts Quoted",
        rendererType: RendererType.CURRENCY,
        minWidth: "16ch",
        maxWidth: "16ch",
        width: "16ch",
        align: "right",
      },
      {
        name: "Parts Markdown",
        rendererType: RendererType.CURRENCY,
        minWidth: "75px",
        maxWidth: "75px",
        width: "75px",
        align: "right",
      },
      {
        name: "TechID",
        minWidth: "100px",
        maxWidth: "100px",
        width: "100px",
        align: "right",
      },
      {
        name: "Repair > MSRP",
        minWidth: "70px",
        maxWidth: "70px",
        width: "70px",
        align: "right",
      },
      {
        name: "RepairTime (Mins)",
        minWidth: "80px",
        maxWidth: "80px",
        width: "80px",
        align: "right",
      },
      { name: "Appliance Type", align: "right" },
      { name: "Planning Area", align: "right" },
      { name: "JobCode Quoted", align: "right" },
      {
        name: "Customer Rating",
        minWidth: "100px",
        maxWidth: "100px",
        width: "100px",
        align: "right",
      },
      {
        name: "Action",
        minWidth: "60px",
        maxWidth: "60px",
        width: "60px",
        align: "right",
      },
      {
        name: "Justification",
        minWidth: "400px",
        maxWidth: "400px",
        width: "400px",
        align: "left",
      },
    ];

    if (error) {
      return (
        <Box p={3}>
          <Alert severity="error">{error}</Alert>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          bgcolor: "#f8fafc",
          minHeight: "100vh",
        }}
      >
        {/* Filters */}
        <FilterBar
          config={{
            showAcctgDt: true,
            showPlanningArea: true,
            showTechLdap: true,
            showQuotedJobcode: true,
            showDeclineReason: true,
            showLlmClassification: true,
            showCreateSource: true,
            showApplianceType: true,
            showOrderNumber: true,
            showCustomerRating: true,
          }}
          initialValues={localFilters}
          onFilterChange={handleFilterChange}
        />

        {/* Main Content */}
        <Box sx={{ p: { xs: 2, md: 1.75 } }}>
          <Grid container>
            <Grid item xs={12}>
              <Box>
                {/* DataTable */}
                <DataTable
                  data={edFeed}
                  loading={loading || refreshing}
                  maxHeight="600px"
                  title={undefined}
                  columnConfigs={columnConfigs}
                  totalCount={totalCount}
                  page={filterState.page}
                  rowsPerPage={filterState.limit}
                  onPageChange={(_, newPage) => {
                    onFilterChange(
                      true,
                      filterState.startDate,
                      filterState.endDate,
                      filterState.filter,
                      newPage,
                      filterState.limit
                    );
                  }}
                  onRowsPerPageChange={(event) => {
                    onFilterChange(
                      true,
                      filterState.startDate,
                      filterState.endDate,
                      filterState.filter,
                      0,
                      parseInt(event.target.value, 10)
                    );
                  }}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  onRowClick={handleRowClick}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Row Details Dialog */}
        <RowDetailsDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          rowData={selectedRow}
        />
      </Box>
    );
  }
);

ByServiceOrder.displayName = "ByServiceOrder";

export default ByServiceOrder;
